import React from 'react'
import { useWaitingFilesCount } from '../../WaitingFilesCountContext'
import type { CompaniesContextValue } from '../types'
import { companiesReducer, defaultCompaniesReducerState } from '../reducer'
import { useCompanySearchInputs } from './useCompanySearchInputs'
import { MAX_SELECTED_CHILDREN } from '../constants'
import { isAllAccountsOption } from '../helpers/selection'
import { useCompanyQueries } from './useCompanyQueries'
import { useCompanySelection } from './useCompanySelection'

const useCompaniesProviderState = (): CompaniesContextValue => {
  const [state, dispatch] = React.useReducer(companiesReducer, defaultCompaniesReducerState)
  const { parentSearch, childrenSearch, resetChildrenSearch } = useCompanySearchInputs()

  const {
    displayValue: parentSearchValue,
    setDisplayValue: setParentSearchValue,
    committedValue: parentSearchFilterValue,
    setCommittedValue: setParentSearchFilterValue,
  } = parentSearch

  const {
    displayValue: childrenSearchValue,
    setDisplayValue: setChildrenSearchValue,
    committedValue: committedChildrenSearchValue,
    setCommittedValue: setChildrenSearchFilterValue,
  } = childrenSearch

  const {
    parentCompanies,
    orphanCompanies,
    childrenCompanies,
    selectedParent,
    filteringCompaniesIds,
    isAllAccountsSelected,
    isOrphanSelected,
  } = state

  const selectionLimit = MAX_SELECTED_CHILDREN
  const selectableChildren = React.useMemo(
    () => childrenCompanies.filter((company) => !isAllAccountsOption(company)),
    [childrenCompanies],
  )

  const {
    parentQuery,
    orphanQuery,
    childrenQuery,
    parentChildrenCount,
    isUsingRemoteChildren,
    userCompany,
    b2cAutoSelectedChildId,
    b2cAutoSelectedChild,
    isB2CNoFamily,
    isB2CBootstrapLoading,
    isLoginB2C,
  } = useCompanyQueries(dispatch, {
    parentSearchValue: parentSearchFilterValue,
    childrenSearchValue: committedChildrenSearchValue,
    selectedParent,
    isOrphanSelected,
  })

  const { setWaitingFilesCount } = useWaitingFilesCount()
  const resetWaitingFiles = React.useCallback(() => {
    setWaitingFilesCount(0)
  }, [setWaitingFilesCount])

  const {
    setSelectedParent,
    setPendingSelection,
    validateSelection,
    pendingSelectionState,
    validatedSelectionState,
    pendingSelectionIsAllAccounts,
    hasPendingSelection,
    canSelectAllChildren,
    pendingSelectionLimitReached,
    effectiveChildrenLoading,
  } = useCompanySelection({
    state,
    dispatch,
    selectionLimit,
    selectableChildren,
    childrenQuery,
    orphanQuery,
    isUsingRemoteChildren,
    parentChildrenCount,
    userCompany,
    b2cAutoSelectedChildId,
    b2cAutoSelectedChild,
    isLoginB2C,
    resetChildrenSearch,
    setParentSearchValue,
    setParentSearchFilterValue,
    onResetWaitingFiles: resetWaitingFiles,
  })

  const isInitialParentsLoading = parentQuery.isLoading && parentCompanies.length === 0
  const isLoading = isInitialParentsLoading || isB2CBootstrapLoading
  const isError = parentQuery.isError || orphanQuery.isError || childrenQuery.isError

  return {
    parentCompanies,
    orphanCompanies,
    childrenCompanies,
    selectedParent,
    filteringCompaniesIds,
    isAllAccountsSelected,
    isOrphanSelected,
    pendingAllAccountsSelection: pendingSelectionIsAllAccounts,
    hasPendingSelection,
    pendingSelectionLimit: selectionLimit,
    pendingSelectionLimitReached,
    canSelectAllChildren,
    pendingSelectionState,
    validatedSelectionState,
    parentSearchValue,
    setParentSearchValue,
    setParentSearchFilterValue,
    childrenSearchValue,
    setChildrenSearchValue,
    setChildrenSearchFilterValue,
    isParentsFetching: parentQuery.isFetching,
    setSelectedParent,
    setPendingSelection,
    validateSelection,
    isLoading,
    isChildrenLoading: effectiveChildrenLoading,
    isB2CNoFamily,
    isError,
  }
}

export { useCompaniesProviderState }
